import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { FeedbackKind, FeedbackStatus } from '@/enums/feedback'
import { chime, notifyBrowser } from '@/libs/chime'
import { compareFeedback, feedbackCategoryLabel, feedbackSummary, withSuggestions } from '@/libs/feedback'
import i18n from '@/libs/i18n'
import { useFetchStore } from '@/stores/fetch'
import type { IFeedback, IFeedbackSummary } from '@/types/Feedback'

/*
 * THE GUEST FEEDBACK of the tenant being worked in ("Pohvale, pritožbe in predlogi", docs/API.md §18.4),
 * live. One list for the whole console, so the page and the menu's badge (the count of `new`)
 * agree: GET …/feedback, then the guest-requests event stream (`stores/requests.ts`, which
 * follows the tenant) hands every `event: feedback` here and reads the list again on every
 * (re)connect. The list holds every open one and the others of the last `days` days; the page's
 * filters by status, kind and category are applied to it. The overview of the same window
 * (GET …/feedback/summary) is kept beside it for the headline figures and Analiza.
 *
 * A NEW complaint rings (a falling tone) and shows a browser notification where allowed —
 * complaints during the stay are the ones the house can still put right. Praise and suggestions
 * arrive quietly.
 */
export const useFeedbackStore = defineStore('feedback', () => {
    const fetchStore = useFetchStore()

    const tid    = ref('')
    const items  = ref<IFeedback[]>([])
    const loaded = ref(false)
    const days   = ref(30)
    const sound  = ref(true)

    const summary = ref<IFeedbackSummary | null>(null)

    const newCount = computed((): number => items.value.filter((item) => item.status === FeedbackStatus.New).length)

    const sorted = computed((): IFeedback[] => [...items.value].sort(compareFeedback))

    const load = async () => {
        const forTid = tid.value
        if (!forTid) return

        const { statusCode, data } = await fetchStore.fetch(`/tenants/${forTid}/feedback?days=${days.value}`).get().json<IFeedback[]>()

        if (tid.value !== forTid || statusCode.value !== 200) return

        items.value  = data.value ?? []
        loaded.value = true
    }

    /** The overview of the list's window, read again when the window, the list or a feedback changes. */
    const loadSummary = async () => {
        const forTid  = tid.value
        const forDays = days.value
        if (!forTid) return

        const { statusCode, data } = await fetchStore.fetch(`/tenants/${forTid}/feedback/summary?days=${forDays}`).get().json<IFeedbackSummary>()

        if (tid.value !== forTid || days.value !== forDays || statusCode.value !== 200 || !data.value) return

        summary.value = withSuggestions(data.value)
    }

    /** Another window of days for the closed ones (the open ones are always in). */
    const setDays = (value: number) => {
        days.value = value
        void load()
    }

    /** A feedback as it is now: replaced in place, or added — and announced when it is a new complaint. */
    const upsert = (feedback: IFeedback, live = false) => {
        const index = items.value.findIndex((item) => item.id === feedback.id)

        if (index >= 0) {
            items.value.splice(index, 1, feedback)

            return
        }

        items.value = [feedback, ...items.value]

        if (live && feedback.kind === FeedbackKind.Complaint && feedback.status === FeedbackStatus.New) {
            if (sound.value) chime([1320, 660])

            notifyBrowser(
                `#${feedback.number} ${i18n.global.t('feedback_kind.complaint')} — ${feedback.room_name}`,
                [feedbackCategoryLabel(feedback.category), feedbackSummary(feedback)].filter(Boolean).join(': '),
                `feedback-${feedback.id}`,
            )
        }
    }

    /** Follows a tenant (the requests store calls it for the roles that may see feedback); '' stops. */
    const follow = (forTid: string) => {
        if (forTid === tid.value) return

        tid.value    = forTid
        items.value   = []
        loaded.value  = false
        summary.value = null

        if (forTid) void load()
    }

    return { tid, items, loaded, days, sound, summary, newCount, sorted, load, loadSummary, setDays, upsert, follow }
})
